import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Typography,
  Select,
  Button,
  Table,
  Space,
  Modal,
  Input,
  message,
  Row,
  Col,
  Popconfirm,
  Tooltip,
  Transfer,
  Form
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  UsergroupAddOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { TransferProps } from 'antd/es/transfer';

// Import services
import courseService from '../../services/courseService';
import * as subjectService from '../../services/subjectService';
import classService from '../../services/classService';
import sinhVienService from '../../services/sinhVienService';
import nhomThucHanhService from '../../services/nhomThucHanhService';

// Import types
import type { Course, Subject, NhomThucHanh, SinhVien, Class } from '../../../../shared/types';

const { Title } = Typography;
const { Option } = Select;

interface AutoGroupResult {
  MaNhom: string;
  TenNhom: string;
  SoSinhVien: number;
  DanhSachSinhVien: string;
}

const NhomThucHanhPage: React.FC = () => {
  // State for filters
  const [courses, setCourses] = useState<Course[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [classes, setClasses] = useState<Class[]>([]);
  
  const [selectedCourse, setSelectedCourse] = useState<string | undefined>();
  const [selectedSubject, setSelectedSubject] = useState<string | undefined>();
  const [selectedClass, setSelectedClass] = useState<string | undefined>();

  // State for data
  const [practiceGroups, setPracticeGroups] = useState<NhomThucHanh[]>([]);
  const [loading, setLoading] = useState(false);
  const [studentsForGrouping, setStudentsForGrouping] = useState<SinhVien[]>([]);
  
  // Modal states for auto-grouping
  const [autoGroupModalVisible, setAutoGroupModalVisible] = useState(false);
  const [numberOfGroups, setNumberOfGroups] = useState<number>(2);
  const [autoGroupResult, setAutoGroupResult] = useState<AutoGroupResult[]>([]);
  const [resultModalVisible, setResultModalVisible] = useState(false);
  const [studentManagementModalVisible, setStudentManagementModalVisible] = useState(false);
  const [editingGroup, setEditingGroup] = useState<NhomThucHanh | null>(null);
  const [studentModalVisible, setStudentModalVisible] = useState(false);
  const [targetKeys, setTargetKeys] = useState<string[]>([]);
  const [sourceStudents, setSourceStudents] = useState<any[]>([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [form] = Form.useForm();

  // Load initial data for filters
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const courseRes = await courseService.getCourses({ page: 1, limit: 1000 });
        setCourses(courseRes.data || []);
      } catch (error) {
        message.error("Không thể tải danh sách khóa học.");
      }
    };
    loadInitialData();
  }, []);

  const fetchPracticeGroups = useCallback(async () => {
    if (!selectedCourse || !selectedSubject) {
      setPracticeGroups([]);
      return;
    }
    setLoading(true);
    try {
      const response = await nhomThucHanhService.getPracticeGroups({
        khoaHocId: selectedCourse,
        monHocId: selectedSubject,
      });
      if (response.success) {
        setPracticeGroups(response.data || []);
      } else {
        message.error(response.error || "Không thể tải danh sách nhóm thực hành.");
        setPracticeGroups([]);
      }
    } catch (error) {
      message.error("Lỗi khi tải danh sách nhóm thực hành.");
      setPracticeGroups([]);
    } finally {
      setLoading(false);
    }
  }, [selectedCourse, selectedSubject]);

  useEffect(() => {
    fetchPracticeGroups();
  }, [fetchPracticeGroups]);

  // Handle cascading filter logic
  const handleCourseChange = async (courseId: string) => {
    setSelectedCourse(courseId);
    setSelectedSubject(undefined);
    setSelectedClass(undefined);
    setSubjects([]);
    setClasses([]);
    setPracticeGroups([]);

    if (courseId) {
      try {
        const [subjectRes, classRes] = await Promise.all([
          subjectService.listSubjects({ khoaHocId: courseId, limit: 1000 }),
          classService.listClasses({ khoaHocId: courseId, limit: 1000 })
        ]);
        setSubjects(subjectRes.data || []);
        setClasses(classRes.data || []);
      } catch (error) {
        message.error("Không thể tải danh sách môn học hoặc lớp học.");
      }
    }
  };
  
  const handleSubjectChange = (subjectId: string) => {
    setSelectedSubject(subjectId);
    setSelectedClass(undefined);
    setPracticeGroups([]);
  };

  const handleClassChange = (classId: string) => {
    setSelectedClass(classId);
  };
  
  const handleFetchStudents = async () => {
    if (!selectedClass) {
      message.error("Vui lòng chọn lớp học trước.");
      return;
    }
    setLoading(true);
    try {
      const response = await sinhVienService.getByClass(selectedClass, { limit: 1000 });
      if (response.success && response.data) {
        setStudentsForGrouping(response.data || []);
        message.success(`Đã tải ${response.data.length} sinh viên.`);
      } else {
        message.error(response.error || "Không thể tải danh sách sinh viên.");
      }
    } catch (error) {
      message.error("Lỗi khi tải danh sách sinh viên.");
    } finally {
      setLoading(false);
    }
  };

  const handleAutoGroupClick = () => {
    if (studentsForGrouping.length === 0) {
      message.warning("Vui lòng lấy danh sách sinh viên trước khi chia nhóm.");
      return;
    }
    setAutoGroupModalVisible(true);
  };
  
  const handleAutoGroup = () => {
    if (numberOfGroups <= 0 || studentsForGrouping.length === 0) {
      return;
    }
    
    // Sort students by MaHS instead of shuffling
    const sortedStudents = [...studentsForGrouping].sort((a, b) => 
      a.MaHS.localeCompare(b.MaHS)
    );
    
    // Divide students sequentially (chunking)
    const groups: SinhVien[][] = [];
    const totalStudents = sortedStudents.length;
    const baseSize = Math.floor(totalStudents / numberOfGroups);
    let remainder = totalStudents % numberOfGroups;
    let currentIndex = 0;

    for (let i = 0; i < numberOfGroups; i++) {
      let groupSize = baseSize + (remainder > 0 ? 1 : 0);
      groups.push(sortedStudents.slice(currentIndex, currentIndex + groupSize));
      currentIndex += groupSize;
      if (remainder > 0) {
        remainder--;
      }
    }

    const subject = subjects.find(s => s.MonHocID === selectedSubject);
    const classObj = classes.find(c => c.LopHocID === selectedClass);

    if (!subject || !classObj) {
      message.error("Vui lòng chọn đầy đủ Môn học và Lớp học.");
      return;
    }
    
    const subjectCode = selectedSubject || '';
    const classCode = selectedClass?.slice(-5) || '';
    const subjectShort = subject.TenMonHoc.split(' ').map((w: string) => w[0]).join('').toUpperCase();
    const className = classObj.TenLop;

    const results = groups.map((group, index) => {
      const stt = (index + 1).toString().padStart(2, '0');
      
      const maNhom = `${subjectCode}${classCode}${stt}`.slice(0, 20);
      const tenNhom = `${subjectShort}-${className}-${stt}`;

      return {
        MaNhom: maNhom,
        TenNhom: tenNhom,
        SoSinhVien: group.length,
        DanhSachSinhVien: group.map(s => s.MaHS).join(', '),
      }
    });
    
    setAutoGroupResult(results);
    setAutoGroupModalVisible(false);
    setResultModalVisible(true);
  };
  
  const handleSaveAutoGroup = async () => {
    if (!selectedClass || !selectedCourse || !selectedSubject || autoGroupResult.length === 0) {
      message.error("Thiếu thông tin cần thiết để lưu nhóm.");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        groups: autoGroupResult,
        lopHocId: selectedClass,
        khoaHocId: selectedCourse,
        monHocId: selectedSubject
      };
      
      const response = await nhomThucHanhService.createBatch(payload);
      
      if (response.success) {
        message.success("Tạo nhóm thực hành thành công!");
        setResultModalVisible(false);
        fetchPracticeGroups();
      } else {
        message.error(response.error || "Không thể lưu nhóm thực hành.");
      }
    } catch (error) {
      message.error("Lỗi khi lưu các nhóm thực hành.");
    } finally {
      setLoading(false);
    }
  };
  
  const handleManageStudents = async (group: NhomThucHanh) => {
    setEditingGroup(group);
    setStudentModalVisible(true);
    setLoadingStudents(true);
    try {
      if (!group.LopHocID || !group.MonHocID) {
        message.error("Nhóm thực hành này thiếu thông tin Lớp học hoặc Môn học.");
        setLoadingStudents(false);
        return;
      }
      
      const [enrolledRes, availableRes] = await Promise.all([
        nhomThucHanhService.getStudentsInGroup(group.MaNhomThucHanh),
        nhomThucHanhService.getAvailableStudents(group.LopHocID, group.MonHocID)
      ]);
      
      const enrolledStudents = (enrolledRes.success && enrolledRes.data) ? enrolledRes.data.map((s: any) => ({ key: s.MaHS, title: `${s.Hoten} (${s.MaHS})` })) : [];
      const availableStudents = (availableRes.success && availableRes.data) ? availableRes.data.map((s: any) => ({ key: s.MaHS, title: `${s.Hoten} (${s.MaHS})` })) : [];
      
      setTargetKeys(enrolledStudents.map((s: any) => s.key));
      setSourceStudents([...availableStudents, ...enrolledStudents]);
    } catch (error) {
      message.error("Lỗi khi tải danh sách sinh viên.");
    } finally {
      setLoadingStudents(false);
    }
  };

  const handleStudentSave = async () => {
    if (!editingGroup) return;
    setLoadingStudents(true);
    try {
      await nhomThucHanhService.updateStudentsInGroup(editingGroup.MaNhomThucHanh, targetKeys);
      message.success("Cập nhật sinh viên thành công!");
      setStudentModalVisible(false);
    } catch (error) {
      message.error("Lỗi khi cập nhật sinh viên.");
    } finally {
      setLoadingStudents(false);
    }
  };

  const handleDelete = async (group: NhomThucHanh) => {
    try {
      setLoading(true);
      const response = await nhomThucHanhService.deleteGroup(group.MaNhomThucHanh);
      if (response.success) {
        message.success("Xóa nhóm thành công!");
        fetchPracticeGroups();
      } else { message.error(response.error || "Lỗi khi xóa."); }
    } catch (error) {
      message.error("Lỗi khi xóa nhóm.");
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (group: NhomThucHanh) => {
    setEditingGroup(group);
    form.setFieldsValue({
      TenNhomThucHanh: group.TenNhomThucHanh,
    });
    setEditModalVisible(true);
  };

  const handleEditSave = async () => {
    if (!editingGroup) return;
    try {
      const values = await form.validateFields();
      setLoading(true);
      const response = await nhomThucHanhService.updateGroup(editingGroup.MaNhomThucHanh, {
        TenNhomThucHanh: values.TenNhomThucHanh,
      });

      if (response.success) {
        message.success("Cập nhật nhóm thành công!");
        setEditModalVisible(false);
        fetchPracticeGroups();
      } else {
        message.error(response.error || "Lỗi khi cập nhật.");
      }
    } catch (error) {
      message.error("Không thể lưu thay đổi.");
    } finally {
      setLoading(false);
    }
  };

  const columns: ColumnsType<NhomThucHanh> = [
    { title: 'Mã nhóm', dataIndex: 'MaNhomThucHanh', key: 'MaNhomThucHanh' },
    { title: 'Tên nhóm', dataIndex: 'TenNhomThucHanh', key: 'TenNhomThucHanh' },
    { 
      title: 'Khóa học', 
      dataIndex: 'KhoaHocID', 
      key: 'KhoaHocID',
      render: (id) => courses.find(c => c.KhoaHocID === id)?.TenKhoaHoc || id
    },
    { 
      title: 'Lớp học', 
      dataIndex: 'LopHocID', 
      key: 'LopHocID',
      render: (id) => classes.find(c => c.LopHocID === id)?.TenLop || id
    },
    { 
      title: 'Môn học', 
      dataIndex: 'MonHocID', 
      key: 'MonHocID',
      render: (id) => subjects.find(s => s.MonHocID === id)?.TenMonHoc || id
    },
    { title: 'Sinh viên', key: 'sinhvien', render: (_, record) => <Button size="small" icon={<UserOutlined />} onClick={() => handleManageStudents(record)}>Xem DS</Button> },
    {
      title: 'Thao tác',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="Sửa thông tin nhóm">
            <Button size="small" icon={<EditOutlined />} onClick={() => openEditModal(record)} />
          </Tooltip>
          <Tooltip title="Xóa nhóm">
            <Popconfirm title="Bạn có chắc chắn muốn xóa nhóm này?" onConfirm={() => handleDelete(record)} okText="Xóa" cancelText="Hủy">
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Title level={2} style={{ textAlign: 'left' }}>
          Quản lý Nhóm thực hành (chuẩn hóa)
        </Title>
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col><Select placeholder="1. Chọn khóa học" style={{ width: 200 }} onChange={handleCourseChange} value={selectedCourse}>{courses.map(c => <Option key={c.KhoaHocID} value={c.KhoaHocID}>{c.TenKhoaHoc}</Option>)}</Select></Col>
          <Col><Select placeholder="2. Chọn môn học" style={{ width: 200 }} onChange={handleSubjectChange} value={selectedSubject} disabled={!selectedCourse}>{subjects.map(s => <Option key={s.MonHocID} value={s.MonHocID}>{s.TenMonHoc}</Option>)}</Select></Col>
          <Col><Select placeholder="3. Chọn lớp học" style={{ width: 200 }} onChange={handleClassChange} value={selectedClass} disabled={!selectedCourse}>{classes.map(l => <Option key={l.LopHocID} value={l.LopHocID}>{l.TenLop}</Option>)}</Select></Col>
        </Row>
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col>
            <Button 
              icon={<UsergroupAddOutlined />} 
              disabled={!selectedClass}
              onClick={handleFetchStudents}
              loading={loading}
            >
              Lấy danh sách sinh viên
            </Button>
          </Col>
          <Col>
            <Button 
              type="primary" 
              icon={<PlusOutlined />} 
              disabled={studentsForGrouping.length === 0}
              onClick={handleAutoGroupClick}
            >
              Tạo nhóm thực hành tự động
            </Button>
          </Col>
          <Col>
            <Button icon={<ReloadOutlined />} onClick={fetchPracticeGroups}>Làm mới danh sách nhóm</Button>
          </Col>
        </Row>
        
        <Table
          columns={columns}
          dataSource={practiceGroups}
          loading={loading}
          rowKey="MaNhomThucHanh"
          bordered
        />
      </Card>
      
      <Modal
        title="Tạo nhóm thực hành tự động"
        open={autoGroupModalVisible}
        onOk={handleAutoGroup}
        onCancel={() => setAutoGroupModalVisible(false)}
      >
        <p>Nhập số nhóm cần tạo cho {studentsForGrouping.length} sinh viên:</p>
        <Input 
          type="number" 
          value={numberOfGroups}
          onChange={(e) => setNumberOfGroups(parseInt(e.target.value, 10))}
          placeholder="Nhập số nhóm cần tạo" 
        />
      </Modal>

      <Modal
        title="Kết quả chia nhóm"
        open={resultModalVisible}
        onOk={handleSaveAutoGroup}
        onCancel={() => setResultModalVisible(false)}
        width="80%"
        okText="Lưu các nhóm này"
        confirmLoading={loading}
      >
        <Table 
          dataSource={autoGroupResult}
          rowKey="MaNhom"
          columns={[
            { title: 'Mã nhóm', dataIndex: 'MaNhom' },
            { title: 'Tên nhóm', dataIndex: 'TenNhom' },
            { title: 'Số sinh viên', dataIndex: 'SoSinhVien' },
            { title: 'Danh sách sinh viên (Mã HS)', dataIndex: 'DanhSachSinhVien' },
          ]}
        />
      </Modal>

      <Modal
        title={`Quản lý sinh viên - Nhóm ${editingGroup?.TenNhomThucHanh}`}
        open={studentModalVisible}
        onOk={handleStudentSave}
        onCancel={() => setStudentModalVisible(false)}
        width="900px"
        confirmLoading={loadingStudents}
      >
        <Transfer
          dataSource={sourceStudents}
          targetKeys={targetKeys}
          onChange={(newKeys) => setTargetKeys(newKeys as string[])}
          render={item => item.title}
          showSearch
          listStyle={{
            width: 400,
            height: 400,
          }}
          titles={['Chưa vào nhóm', 'Đã vào nhóm']}
        />
      </Modal>

      <Modal
        title="Chỉnh sửa Nhóm thực hành"
        open={editModalVisible}
        onOk={handleEditSave}
        onCancel={() => setEditModalVisible(false)}
        confirmLoading={loading}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="TenNhomThucHanh"
            label="Tên nhóm thực hành"
            rules={[{ required: true, message: 'Vui lòng nhập tên nhóm!' }]}
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default NhomThucHanhPage;